// Route Optimizer — optimize delivery stops for a driver on a given date
import { useState, useCallback } from "react";
import {
  Route,
  PrinterIcon,
  RotateCcw,
  MapPin,
  Clock,
  Package,
  AlertCircle,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useRoutes } from "@/hooks/admin/useRoutes";
import { useDrivers } from "@/hooks/admin/useDrivers";
import type { RouteOptimizePayload } from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat("fr-SN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(iso));
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Routes() {
  const { data: driversData, isLoading: driversLoading } = useDrivers();
  const { optimizedRoute, optimize, isOptimizing, isError, error, reset } =
    useRoutes();

  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [formError, setFormError] = useState<string | null>(null);

  const activeDrivers = driversData?.drivers.filter((d) => d.is_available) ?? [];

  const handleOptimize = useCallback(() => {
    if (!selectedDriverId) {
      setFormError("Veuillez sélectionner un chauffeur.");
      return;
    }
    if (!selectedDate) {
      setFormError("Veuillez sélectionner une date.");
      return;
    }
    setFormError(null);
    const payload: RouteOptimizePayload = {
      driver_id: selectedDriverId,
      date: selectedDate,
    };
    optimize(payload);
  }, [selectedDriverId, selectedDate, optimize]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleReset = useCallback(() => {
    reset();
    setSelectedDriverId("");
    setSelectedDate(todayIso());
    setFormError(null);
  }, [reset]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Route className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Optimiseur de routes
              </h1>
              <p className="text-sm text-stone-500">
                Génère un ordre de passage optimisé pour un chauffeur
              </p>
            </div>
          </div>

          {optimizedRoute && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="gap-1.5 print:hidden"
              >
                <PrinterIcon className="h-4 w-4" />
                Imprimer
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="gap-1.5 print:hidden"
              >
                <RotateCcw className="h-4 w-4" />
                Réinitialiser
              </Button>
            </div>
          )}
        </div>

        {/* Config form — shown before result */}
        {!optimizedRoute && (
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-semibold text-stone-700">
              Paramètres
            </h2>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="mb-1.5 block text-sm font-medium text-stone-700">
                  Chauffeur
                </label>
                <select
                  value={selectedDriverId}
                  onChange={(e) => setSelectedDriverId(e.target.value)}
                  disabled={driversLoading}
                  className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100 disabled:opacity-50"
                >
                  <option value="">Sélectionner un chauffeur…</option>
                  {activeDrivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.full_name} — {d.vehicle_type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="mb-1.5 block text-sm font-medium text-stone-700">
                  Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
                />
              </div>

              <Button
                onClick={handleOptimize}
                disabled={isOptimizing}
                className="shrink-0 gap-1.5"
              >
                {isOptimizing ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Route className="h-4 w-4" />
                )}
                Optimiser la route
              </Button>
            </div>

            {formError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            {isError && (
              <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error instanceof Error
                  ? error.message
                  : "Impossible d'optimiser la route. Veuillez réessayer."}
              </div>
            )}
          </div>
        )}

        {/* Prompt before first optimization */}
        {!optimizedRoute && !isOptimizing && (
          <EmptyState
            icon={<Route className="h-6 w-6" />}
            title="Aucune route générée"
            description="Sélectionnez un chauffeur et une date, puis cliquez sur « Optimiser la route »."
          />
        )}

        {/* Optimized route result */}
        {optimizedRoute && (
          <div className="space-y-4">
            {/* Summary header */}
            <div className="rounded-lg border border-stone-200 bg-white p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-display text-lg font-semibold text-stone-900">
                    {optimizedRoute.driver_name}
                  </h2>
                  <p className="mt-0.5 text-sm text-stone-500">
                    {formatDate(optimizedRoute.date)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-stone-600">
                  {optimizedRoute.total_distance_km != null && (
                    <span>
                      <span className="font-semibold text-stone-900">
                        {optimizedRoute.total_distance_km} km
                      </span>{" "}
                      au total
                    </span>
                  )}
                  {optimizedRoute.estimated_duration_minutes != null && (
                    <span>
                      <span className="font-semibold text-stone-900">
                        {Math.floor(optimizedRoute.estimated_duration_minutes / 60)}h
                        {optimizedRoute.estimated_duration_minutes % 60}min
                      </span>{" "}
                      estimées
                    </span>
                  )}
                  <span>
                    <span className="font-semibold text-stone-900">
                      {optimizedRoute.stops.length}
                    </span>{" "}
                    arrêts
                  </span>
                </div>
              </div>
            </div>

            {/* Stop list */}
            <div className="rounded-lg border border-stone-200 bg-white">
              <div className="border-b border-stone-100 px-5 py-3">
                <h3 className="text-sm font-semibold text-stone-700">
                  Feuille de route
                </h3>
              </div>
              <ol className="divide-y divide-stone-100">
                {optimizedRoute.stops.map((stop) => (
                  <li
                    key={stop.stop_number}
                    className="flex items-start gap-4 px-5 py-4"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-800">
                      {stop.stop_number}
                    </span>

                    <div className="flex-1 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-stone-900">
                          {stop.customer_name}
                        </p>
                        {stop.estimated_time && (
                          <div className="flex items-center gap-1 text-xs text-stone-500">
                            <Clock className="h-3.5 w-3.5" />
                            {stop.estimated_time}
                          </div>
                        )}
                      </div>
                      <div className="flex items-start gap-1.5 text-xs text-stone-600">
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
                        <span>{stop.address}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-stone-500">
                        <Package className="h-3.5 w-3.5 shrink-0 text-stone-400" />
                        <span>Réf. {stop.order_ref}</span>
                      </div>
                      {stop.notes && (
                        <p className="text-xs italic text-stone-400">
                          {stop.notes}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
